
import { useState, useEffect, useImperativeHandle, useRef, useCallback, useMemo } from 'react'
import { useModelWithOverrides } from '@/hooks/useModelWithOverrides'
import { useLibrary } from '@/hooks/useLibrary'
import type { LibraryModel } from '@/hooks/useLibraryModels'
import { updateNodeHandles } from '@/lib/canvas/utils/handleManagement'
import type { HandleMeta } from '@/lib/canvas/types'
import type { NodeSettingsProps, NodeSettingsPanelRef } from './types'
import type { NodeModelSettings, AiModelPurpose } from '@genieartinc/schemas'
import { useCanvasBridgeStore } from '@/stores/useCanvasBridgeStore'
import { modelTitleState } from '@/components/canvas/utils/modelTitle'
import { labelStyleTransferInputParams, orderStyleTransferInputParams, ensureInpaintHandles, labelInpaintInputParams, orderInpaintInputParams } from '@genieartinc/schemas/pools/ai'
import { isModelFreeNodeControl } from './node-control-help'

const DEFAULT_TEXT_SYSTEM_PROMPT = `You are a prompt generator for AI image diffusion models. Given a concept, output a single prompt using diffusion model syntax: comma-separated descriptive tags covering subject, composition, style, lighting, mood, and quality. Output only the raw prompt. No explanations, labels, or commentary.`

export function useNodeSettingsState(
  props: NodeSettingsProps,
  ref: React.ForwardedRef<NodeSettingsPanelRef>
) {
  const { nodeId, nodeType, initialParams, onStateChange, nodeHandles } = props

  // Node handles passed as prop from page (avoids needing ReactFlow context)
  const currentNodeHandles = nodeHandles || []

  const effectiveNodeType = nodeType ?? 'text-llm'
  const modelsEnabled = Boolean(nodeId) && !isModelFreeNodeControl(props.componentType)

  // Model and settings state
  const [selectedModelId, setSelectedModelId] = useState<string>('')
  const [selectedModelFamilySlug, setSelectedModelFamilySlug] = useState<string>('')
  const [localSettings, setLocalSettings] = useState<NodeModelSettings>({})
  const [isModelChanging, setIsModelChanging] = useState(false)
  const [outputMode, setOutputMode] = useState<'in-node' | 'in-canvas'>('in-canvas')
  const [systemPrompt, setSystemPrompt] = useState<string>(effectiveNodeType === 'text-llm' ? DEFAULT_TEXT_SYSTEM_PROMPT : '')

  // Pinned parameters state
  const [pinnedParameters, setPinnedParameters] = useState<string[]>(initialParams?.pinnedParameters ?? [])

  // Single API call: fetch ALL models for nodeType/purpose + selected model's merged parameters
  // Always pass undefined for modelFamilySlug so we get the full list for the picker
  const { allModels, settingsParams, inputParams, isLoading: modelsLoading, error: modelsError, refetch: refetchLibrary } = useModelWithOverrides(
    effectiveNodeType,
    undefined,
    selectedModelId,
    props.purpose,
    props.subPurpose,
    modelsEnabled
  )

  // User's library — only show models the user has added to their library
  const { libraryIds, isLoading: libraryIdsLoading } = useLibrary(modelsEnabled)

  // The user's library models for this node, else every enabled model for it — a node is always
  // wired to a model, even when the library has none of this kind. Admin defaults come first.
  const filteredModels = useMemo(() => {
    const inLibrary = allModels.filter(m => libraryIds.has(m.id))
    const models = inLibrary.length > 0
      ? allModels.filter(m => libraryIds.has(m.id) || m.id === selectedModelId)
      : allModels
    return [...models.filter(m => m.isDefault), ...models.filter(m => !m.isDefault)]
  }, [allModels, libraryIds, selectedModelId])

  // Derive picker-compatible model list from filteredModels (same shape as LibraryModel)
  const libraryModels: LibraryModel[] = useMemo(() =>
    filteredModels.map((m, i) => ({
      modelId: m.id,
      modelName: m.name,
      brandSlug: m.modelFamilySlug ?? null,
      brandName: m.modelFamily ?? null,
      purpose: m.purpose ?? null,
      sortOrder: i,
    })),
    [filteredModels]
  )
  const libraryLoading = modelsLoading || libraryIdsLoading
  const libraryEmpty = !libraryLoading && libraryModels.length === 0
  const libraryError = modelsError

  // Track if settings loaded from initialParams
  const settingsLoadedRef = useRef(false)
  // Track if user explicitly changed model (for unmount flush)
  const modelChangedRef = useRef(false)
  const prevModelRef = useRef<string>('')
  const prevNodeIdRef = useRef<string | undefined>(undefined)
  const savedModelRef = useRef<string | undefined>(undefined)
  const pendingModelRef = useRef<string | null>(null)
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // The saved state is authoritative after pinned edits, undo/redo and selection changes.
  useEffect(() => {
    if (prevNodeIdRef.current === nodeId && savedModelRef.current === initialParams?.modelId) return
    prevNodeIdRef.current = nodeId
    savedModelRef.current = initialParams?.modelId
    if (saveTimeoutRef.current) { clearTimeout(saveTimeoutRef.current); saveTimeoutRef.current = null }
    const isOwnModelChange = pendingModelRef.current === initialParams?.modelId
    if (!isOwnModelChange) { modelChangedRef.current = false; pendingModelRef.current = null }

    if (initialParams?.modelId) {
      settingsLoadedRef.current = !isOwnModelChange
      setSelectedModelFamilySlug(initialParams.modelFamilySlug ?? '')
      setSelectedModelId(initialParams.modelId)
      if (!isOwnModelChange) prevModelRef.current = initialParams.modelId
      setLocalSettings(initialParams.settings ?? {})
      setSystemPrompt(initialParams.systemPrompt ?? (nodeType === 'text-llm' ? DEFAULT_TEXT_SYSTEM_PROMPT : ''))
      setPinnedParameters(initialParams.pinnedParameters ?? [])
    } else {
      settingsLoadedRef.current = false
      setSelectedModelFamilySlug('')
      setSelectedModelId('')
      setLocalSettings(initialParams?.settings ?? {})
      setSystemPrompt(nodeType === 'text-llm' ? DEFAULT_TEXT_SYSTEM_PROMPT : '')
      setPinnedParameters([])
      prevModelRef.current = ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId, initialParams?.modelId])

  // Sync localSettings when external changes arrive (e.g. from pinned UI on node)
  useEffect(() => {
    if (!initialParams?.settings) return
    // Only sync if not currently saving (avoid overwriting local edits)
    if (saveTimeoutRef.current) return
    setLocalSettings(prev => {
      const incoming = initialParams.settings as Record<string, unknown>
      const keys = Object.keys(incoming)
      return keys.length !== Object.keys(prev).length || keys.some(key => prev[key] !== incoming[key])
        ? incoming : prev
    })
  }, [initialParams?.settings])

  const handlePurposeChange = useCallback((newPurpose: AiModelPurpose) => {
    if (saveTimeoutRef.current) { clearTimeout(saveTimeoutRef.current); saveTimeoutRef.current = null }
    onStateChange?.((prevState) => ({
      ...prevState,
      purpose: newPurpose,
      settings: localSettings,
      modelId: undefined,
      modelFamilySlug: undefined,
      modelName: undefined,
    }))
  }, [onStateChange, localSettings])

  // Auto-select first library model when loaded, or fix stale model reference
  useEffect(() => {
    if (!modelsEnabled || libraryLoading || libraryError || libraryModels.length === 0) return

    // Wait for the saved-model synchronization above before considering a fallback.
    if (initialParams?.modelId && selectedModelId !== initialParams.modelId) return

    // No model selected yet — pick the first one
    // Skip if initialParams already loaded a saved model (settingsLoadedRef is set
    // synchronously by the nodeId-change effect before this effect runs, but
    // selectedModelId state hasn't re-rendered yet)
    if (!selectedModelId && !settingsLoadedRef.current) {
      const first = libraryModels[0]
      pendingModelRef.current = first.modelId
      setSelectedModelId(first.modelId)
      setSelectedModelFamilySlug(first.brandSlug ?? '')
      // Persist auto-selected model so it survives deselect/reselect
      onStateChange?.((prevState) => {
        const updates: Record<string, unknown> = {
          ...prevState,
          modelId: first.modelId,
          modelFamilySlug: first.brandSlug ?? '',
        }
        if (first.modelName) Object.assign(updates, modelTitleState(prevState, first.modelName))
        return updates
      })
      return
    }

    // A model absent from the current route/catalog falls back to the first available model.
    if (selectedModelId) {
      const exists = allModels.some(m => m.id === selectedModelId)
      if (!exists) {
        const first = libraryModels[0]
        pendingModelRef.current = first.modelId
        setSelectedModelId(first.modelId)
        setSelectedModelFamilySlug(first.brandSlug ?? '')
        settingsLoadedRef.current = false
        onStateChange?.((prevState) => {
          const updates: Record<string, unknown> = {
            ...prevState,
            modelId: first.modelId,
            modelFamilySlug: first.brandSlug ?? '',
          }
          if (first.modelName) Object.assign(updates, modelTitleState(prevState, first.modelName))
          return updates
        })
      }
    }
  }, [modelsEnabled, libraryModels, libraryLoading, libraryError, selectedModelId, onStateChange, allModels, initialParams?.modelId])

  // Dynamic handle updates — use ref for currentNodeHandles to avoid re-fire loop.
  // The effect should only fire on model change, not on handle updates (which it causes).
  const currentNodeHandlesRef = useRef(currentNodeHandles)
  currentNodeHandlesRef.current = currentNodeHandles

  useEffect(() => {
    if (!selectedModelId || !nodeId || inputParams.length === 0) return
    // Shader nodes derive their handles from the active sketch's declared
    // inputTypes (see ShaderNodeNode). This hook still runs for them because
    // NodeSettingsPanel calls it before branching to ShaderNodeSettings, and
    // without this guard the auto-selected text-llm model's parameters would
    // be grafted onto the shader as text/video inputs.
    if (props.componentType === 'ShaderNode') return

    const handleInputs = props.subPurpose === 'style-transfer'
      ? labelStyleTransferInputParams(orderStyleTransferInputParams(inputParams))
      : props.subPurpose === 'inpaint'
        ? labelInpaintInputParams(orderInpaintInputParams(inputParams))
        : inputParams
    const existingHandles = props.subPurpose === 'inpaint'
      ? ensureInpaintHandles(currentNodeHandlesRef.current, 'inpaint')
      : currentNodeHandlesRef.current
    const { handlesToAdd, handlesToUpdate, handlesToRemove } = updateNodeHandles(
      handleInputs,
      existingHandles
    )

    const handleUpdateCallback = useCanvasBridgeStore.getState().handleUpdateCallback
    if (handleUpdateCallback && typeof handleUpdateCallback === 'function') {
      handleUpdateCallback({ nodeId, handlesToAdd, handlesToUpdate, handlesToRemove })
    }
  }, [selectedModelId, inputParams, nodeId, props.componentType, props.subPurpose])

  // Settings initialization & preservation
  const settingsParamsRef = useRef<typeof settingsParams>([])
  settingsParamsRef.current = settingsParams

  useEffect(() => {
    if (libraryLoading || !selectedModelId) return
    if (settingsLoadedRef.current) {
      prevModelRef.current = selectedModelId
      return
    }

    const modelChanged = prevModelRef.current !== '' && prevModelRef.current !== selectedModelId
    if (modelChanged || (prevModelRef.current === '' && selectedModelId)) {
      const merged: NodeModelSettings = settingsParamsRef.current.length ? {} : { ...localSettings }
      settingsParamsRef.current.forEach((param) => {
        const value = localSettings[param.name]
        const compatible = value !== undefined &&
          (!param.enum || param.enum.includes(value)) &&
          (param.type !== 'integer' || (typeof value === 'number' && Number.isInteger(value))) &&
          (!['string', 'number', 'boolean'].includes(param.type) || typeof value === param.type) &&
          (param.minimum === undefined || typeof value !== 'number' || value >= param.minimum) &&
          (param.maximum === undefined || typeof value !== 'number' || value <= param.maximum)
        if (compatible) merged[param.name] = value
        else if (param.default !== undefined) merged[param.name] = param.default
      })
      setLocalSettings(merged)
      onStateChange?.(previous => previous.modelId === selectedModelId ? { ...previous, settings: merged } : previous)
      pendingModelRef.current = null
    }

    prevModelRef.current = selectedModelId
  }, [selectedModelId, nodeId, libraryLoading, localSettings, onStateChange])

  // Expose imperative API via ref
  const getSettingsRef = useRef(() => ({
    modelId: selectedModelId,
    modelFamilySlug: selectedModelFamilySlug,
    settings: localSettings,
    outputMode,
    systemPrompt: systemPrompt || undefined,
  }))

  // Keep ref closure up to date without triggering re-renders
  getSettingsRef.current = () => ({
    modelId: selectedModelId,
    modelFamilySlug: selectedModelFamilySlug,
    settings: localSettings,
    outputMode,
    systemPrompt: systemPrompt || undefined,
  })

  const loadSnapshotRef = useRef((snapshot: { modelId: string; settings: NodeModelSettings; modelFamilySlug?: string; brandFamily?: string }) => {
    const familySlug = snapshot.modelFamilySlug ?? snapshot.brandFamily
    if (familySlug) setSelectedModelFamilySlug(familySlug)
    if (snapshot.modelId) setSelectedModelId(snapshot.modelId)
    if (snapshot.settings) setLocalSettings(snapshot.settings)
  })

  useImperativeHandle(ref, () => ({
    getSettings: () => getSettingsRef.current(),
    loadSnapshot: (snapshot: { modelId: string; settings: NodeModelSettings; modelFamilySlug?: string; brandFamily?: string }) => loadSnapshotRef.current(snapshot),
  }), [])

  // Register settings API on bridge store via useEffect (NOT during commit phase)
  useEffect(() => {
    useCanvasBridgeStore.getState().registerSettingsApi({
      getSettings: () => getSettingsRef.current(),
      loadSnapshot: (snapshot: { modelId: string; settings: NodeModelSettings; modelFamilySlug?: string; brandFamily?: string }) => loadSnapshotRef.current(snapshot),
    })
    return () => {
      useCanvasBridgeStore.getState().registerSettingsApi(null)
    }
  }, [])

  // --- Handlers ---

  const handleModelChange = useCallback((newModelId: string, brandSlug: string | null) => {
    // Cancel any pending debounced settings save from the previous model
    if (saveTimeoutRef.current) { clearTimeout(saveTimeoutRef.current); saveTimeoutRef.current = null }
    setIsModelChanging(true)
    pendingModelRef.current = newModelId
    setSelectedModelId(newModelId)
    setSelectedModelFamilySlug(brandSlug ?? '')
    settingsLoadedRef.current = false
    modelChangedRef.current = true

    // Update title to model name for AI nodes (only if title is still auto-generated)
    const model = filteredModels.find(m => m.id === newModelId)
    const modelName = model?.name

    onStateChange?.((prevState) => {
      const updates: Record<string, unknown> = {
        ...prevState,
        modelFamilySlug: brandSlug ?? '',
        modelId: newModelId,
        settings: localSettings,
      }
      if (modelName) Object.assign(updates, modelTitleState(prevState, modelName))
      return updates
    })
    setTimeout(() => setIsModelChanging(false), 300)
  }, [onStateChange, filteredModels, localSettings])

  // Debounced save refs
  const systemPromptTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const selectedModelFamilySlugRef = useRef(selectedModelFamilySlug)
  selectedModelFamilySlugRef.current = selectedModelFamilySlug
  const selectedModelIdRef = useRef(selectedModelId)
  selectedModelIdRef.current = selectedModelId
  const localSettingsRef = useRef(localSettings)
  localSettingsRef.current = localSettings
  const onStateChangeRef = useRef(onStateChange)
  onStateChangeRef.current = onStateChange
  const systemPromptRef = useRef(systemPrompt)
  systemPromptRef.current = systemPrompt

  const handleSettingsChange = useCallback((key: string, value: unknown) => {
    setLocalSettings((prev) => {
      const newSettings = { ...prev, [key]: value }
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = setTimeout(() => {
        saveTimeoutRef.current = null
        // Use refs to read current values — avoids stale closure if model changes during debounce
        onStateChangeRef.current?.((prevState) => ({
          ...prevState,
          modelFamilySlug: selectedModelFamilySlugRef.current,
          modelId: selectedModelIdRef.current,
          settings: newSettings,
        }))
      }, 500)
      return newSettings
    })
  }, [])

  const handleSystemPromptChange = useCallback((value: string) => {
    setSystemPrompt(value)
    if (systemPromptTimeoutRef.current) clearTimeout(systemPromptTimeoutRef.current)
    // Persist immediately — user explicitly clicks Save
    onStateChange?.((prevState) => ({ ...prevState, systemPrompt: value || undefined }))
  }, [onStateChange])

  const handlePinToggle = useCallback((paramName: string) => {
    setPinnedParameters((prev) => {
      const next = prev.includes(paramName)
        ? prev.filter((n) => n !== paramName)
        : [...prev, paramName]
      queueMicrotask(() => {
        onStateChangeRef.current?.((prevState: Record<string, unknown>) => ({
          ...prevState,
          pinnedParameters: next,
          modelId: selectedModelIdRef.current,
          modelFamilySlug: selectedModelFamilySlugRef.current,
          settings: localSettingsRef.current,
        }))
      })
      return next
    })
  }, [])

  /** Batch-set all pinned parameters at once (used by Customize Node popover) */
  const setPinnedParametersBatch = useCallback((next: string[]) => {
    setPinnedParameters(next)
    queueMicrotask(() => {
      onStateChangeRef.current?.((prevState: Record<string, unknown>) => ({
        ...prevState,
        pinnedParameters: next,
        modelId: selectedModelIdRef.current,
        modelFamilySlug: selectedModelFamilySlugRef.current,
        settings: localSettingsRef.current,
      }))
    })
  }, [])

  // Flush pending saves on unmount — fires if debounced saves pending OR model was changed.
  // The modelChangedRef guard prevents re-render loops for no-op unmounts while ensuring
  // model changes survive React's batched state updates on quick deselect.
  useEffect(() => {
    const systemPromptTimeout = systemPromptTimeoutRef.current
    return () => {
      const hadPendingSave = !!saveTimeoutRef.current
      const hadPendingPrompt = !!systemPromptTimeout
      const hadModelChange = modelChangedRef.current
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
      if (systemPromptTimeout) clearTimeout(systemPromptTimeout)
      if (!hadPendingSave && !hadPendingPrompt && !hadModelChange) return
      onStateChangeRef.current?.((prevState: Record<string, unknown>) => ({
        ...prevState,
        modelFamilySlug: selectedModelFamilySlugRef.current,
        modelId: selectedModelIdRef.current,
        settings: localSettingsRef.current,
        systemPrompt: systemPromptRef.current || undefined,
      }))
    }
  }, [])

  return {
    effectiveNodeType,
    libraryModels,
    libraryLoading,
    libraryEmpty,
    libraryError,
    refetchLibrary,
    selectedModelId,
    selectedModelName: allModels.find(model => model.id === selectedModelId)?.name,
    selectedModelDescription: allModels.find(model => model.id === selectedModelId)?.description,
    handleModelChange,
    settingsParams,
    inputParams,
    localSettings,
    isModelChanging,
    handleSettingsChange,
    systemPrompt,
    handleSystemPromptChange,
    pinnedParameters,
    handlePinToggle,
    setPinnedParametersBatch,
    handlePurposeChange,
  }
}
